// Inspired by https://github.com/mui-org/material-ui/blob/master/docs/src/pages/components/tables/EnhancedTable.tsx

import React, { ReactNode } from 'react';
import {
  Box,
  Checkbox,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Theme,
} from '@mui/material';

interface DataGridHeadProps<Row, ColumnKey extends Extract<keyof Row, string>> {
  columns: readonly ColumnDefinition<Row, ColumnKey>[];
  onRequestSort: (property: ColumnKey) => void;
  sortColumn: ColumnKey;
  isSortAscending: boolean;
}

function DataGridHead<Row, ColumnKey extends Extract<keyof Row, string>>(props: DataGridHeadProps<Row, ColumnKey>) {
  const { columns, onRequestSort, sortColumn, isSortAscending } = props;
  const createSortHandler = (property: ColumnKey) => () => {
    onRequestSort(property);
  };

  return (
    <TableHead>
      <TableRow sx={{ bgcolor: 'backContrast.light' }}>
        <TableCell sx={{ px: 1, py: 0, whiteSpace: 'nowrap' }}>Select</TableCell>
        {columns.map((column) => (
          <TableCell
            key={column.field}
            sx={{ px: 1, py: 0, whiteSpace: 'nowrap' }}
            sortDirection={sortColumn === column.field ? (isSortAscending ? 'asc' : 'desc') : false}
          >
            <TableSortLabel
              active={sortColumn === column.field}
              direction={sortColumn === column.field ? (isSortAscending ? 'asc' : 'desc') : 'asc'}
              onClick={createSortHandler(column.field)}
            >
              {column.headerName}
              {sortColumn === column.field ? (
                <Box
                  component="span"
                  sx={{
                    border: 0,
                    clip: 'rect(0 0 0 0)',
                    height: 1,
                    margin: -1,
                    overflow: 'hidden',
                    p: 0,
                    position: 'absolute',
                    top: 20,
                    width: 1,
                  }}
                >
                  {isSortAscending ? 'sorted ascending' : 'sorted descending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export interface ValueFormatterParametersStrict<Row, Value> {
  value: Value;
  row: Row;
}

export interface ValueFormatterParameters<Row> {
  value: any;
  row: Row;
}

export interface ColumnDefinition<Row, ColumnKey extends Extract<keyof Row, string>> {
  field: ColumnKey;
  headerName: string;
  valueFormatter?: (params: ValueFormatterParameters<Row>) => string;
  renderCell?: (params: ValueFormatterParameters<Row>) => React.ReactNode;
}

interface DataGridProps<Row, ColumnKey extends Extract<keyof Row, string>> {
  rows: readonly Row[];
  columns: readonly ColumnDefinition<Row, ColumnKey>[];
  onRequestSort: (property: ColumnKey) => void;
  sortColumn: ColumnKey;
  isSortAscending: boolean;
  setPageIndex: (pageIndex: number) => void;
  pageIndex: number;
  pageSize: number;
  isRowSelected: (row: Row) => boolean;
  onSelectRow: (row: Row) => void;
  getRowSx: (row: Row) => SxProps<Theme>;
}

export default function DataGrid<Row, ColumnKey extends Extract<keyof Row, string>>(
  props: DataGridProps<Row, ColumnKey>,
) {
  const {
    rows,
    columns,
    onRequestSort,
    sortColumn,
    isSortAscending,
    isRowSelected,
    onSelectRow,
    pageIndex,
    pageSize,
    setPageIndex,
    getRowSx,
  } = props;

  const emptyRows = pageSize - Math.min(pageSize, rows.length - pageIndex * pageSize);

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%' }}>
        <TableContainer>
          <Table sx={{ minWidth: 750, bgcolor: 'backContrast.main' }}>
            <DataGridHead
              columns={columns}
              onRequestSort={onRequestSort}
              sortColumn={sortColumn}
              isSortAscending={isSortAscending}
            />
            <TableBody>
              {rows.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize).map((row, index) => {
                const isItemSelected = isRowSelected(row);

                const rowSx = getRowSx(row);
                return (
                  <TableRow
                    sx={[
                      {
                        '&.Mui-selected:hover': {
                          backgroundColor: '#f4d166',
                        },
                      },
                      ...(Array.isArray(rowSx) ? rowSx : [rowSx]),
                      { borderBottom: 2, borderColor: 'backContrast.dark' },
                      isItemSelected ? { '& td': { color: '#333' } } : {},
                    ]}
                    key={index}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={isItemSelected} onChange={() => onSelectRow(row)} />
                    </TableCell>
                    {columns.map((column) => (
                      <TableCell key={column.field} sx={{ p: 1 }}>
                        {column.renderCell
                          ? column.renderCell({ value: row[column.field], row })
                          : column.valueFormatter
                            ? column.valueFormatter({ value: row[column.field], row })
                            : // TODO Figure out best way to ensure that this is actually a ReactNode.
                              (row[column.field] as unknown as ReactNode)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={columns.length} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          sx={{ bgcolor: 'backContrast.light' }}
          component="div"
          count={rows.length}
          rowsPerPage={pageSize}
          page={pageIndex}
          onPageChange={(_, page) => setPageIndex(page)}
          rowsPerPageOptions={[pageSize]}
        />
      </Paper>
    </Box>
  );
}
